import { useState } from "react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

import { Markdown } from "../misc/Markdown";
import type { CultureDocumentCurrent } from "../types";
import { CultureDocumentHistory } from "./CultureDocumentHistory";

/**
 * Almikia Epic EO (TASK-099/TASK-100): one "Nuestra cultura" document -- a
 * sentence-case title (the seed titles, "Visión" / "Misión" / "Valores" /
 * "Manifiesto", are already a single capitalized word, so no further
 * transform is applied) and its body rendered through the shared sanitized
 * {@link Markdown} renderer (marked + DOMPurify,
 * `src/components/atomic-crm/misc/Markdown.tsx`) -- NEVER a raw HTML path,
 * since this content is direccion-authored and shown to every role
 * (non_functional_requirements.security).
 *
 * "Editar" is shown only when `canEdit` (direccion, mirroring
 * `canAccess.ts`'s `culture_document_versions`/"create" gate) -- every
 * other role sees no edit control, on every screen size (the ticket's own
 * acceptance criterion). "Historial" is shown to every role: every role
 * already reads `culture_document_versions` (SELECT is open to every
 * authenticated role, TASK-096), it only shows past content read-only.
 */
export const CultureDocumentSection = ({
  document,
  canEdit,
}: {
  document: CultureDocumentCurrent;
  canEdit: boolean;
}) => {
  const translate = useTranslate();
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <section className="flex flex-col gap-2 rounded-2xl bg-card p-4 shadow-sm">
      <h2 className="text-xl font-semibold">{document.title}</h2>
      <Markdown className="text-base">{document.body_markdown}</Markdown>
      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11"
          onClick={() => setHistoryOpen(true)}
        >
          {translate("crm.culture.action.history")}
        </Button>
        {canEdit ? (
          <Button type="button" size="sm" className="min-h-11" asChild>
            <Link to={`/almikia/cultura/${document.id}/editar`}>
              {translate("crm.culture.action.edit")}
            </Link>
          </Button>
        ) : null}
      </div>
      <CultureDocumentHistory
        documentId={document.id}
        documentTitle={document.title}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </section>
  );
};
